'use client'

import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'

import useIsAdmin from '~/auth/hooks/useIsAdmin'
import useIsReadOnly from '~/auth/hooks/useIsReadOnly'
import Link from '~/components/Link'
import { Overlay, createOverlay, useOverlayContext } from '~/components/Overlay'
import { useGetRunQuery } from '~/data/api/client/workflows'
import { useWorkflowsEnabled } from '~/data/api/hooks/organizations'
import useFormatters from '~/data/formatters'
import routeDirectory from '~/navigation/routes'
import { usePaletteColor } from '~/theme/hooks'

import type { DisplayNotification } from '../useMergedNotifications'
import NotificationBody from './NotificationBody'
import PriorityPill from './PriorityPill'

type MergedNotificationDetailOverlayProps = {
  notification?: DisplayNotification
}

/**
 * Read-only detail view for a single notification. Opened by clicking a row in
 * {@link MergedNotificationsTable}, it surfaces the full message body (which is
 * truncated in the table) and a link to the source workflow when the viewer can
 * open it.
 */
const MergedNotificationDetailOverlay = createOverlay(({ notification }: MergedNotificationDetailOverlayProps) => {
  const t = useTranslations('notifications.MergedNotificationDetailOverlay')
  const tTable = useTranslations('notifications.MergedNotificationsTable')
  const format = useFormatters()
  const backgroundColor = usePaletteColor('primary', 0.1)
  const { closeOverlay } = useOverlayContext()

  const { enabled: workflowsUiEnabled } = useWorkflowsEnabled()
  const isAdmin = useIsAdmin()
  const readOnly = useIsReadOnly()
  // The link's destination is the workflow builder, which is admin-only behind
  // the workflows gate and has no read-only view — mirror its ACL so those
  // users get plain text instead of a link to NotFound/NotAuthorized.
  const canOpenWorkflow = workflowsUiEnabled && isAdmin && !readOnly

  // The run fetch doubles as the org-access probe: the workflows API 404s runs
  // whose owning workflow is outside the caller's org subtree, so a recipient
  // who can't view the workflow gets plain text instead of a dead link.
  const workflowRunId = canOpenWorkflow ? notification?.workflowRunId : undefined
  // `currentData` (not `data`) so a skipped or re-argued query can never
  // surface another notification's cached run as this one's link.
  const { currentData: run } = useGetRunQuery({ runId: workflowRunId ?? '' }, { skip: !workflowRunId })

  if (!notification) return null

  const sourceHref = run ? routeDirectory.workflows.edit(run.workflowId) : undefined

  return (
    <Overlay fullWidth maxWidth="sm">
      <Overlay.Header prefix={<PriorityPill priority={notification.priority} />} title={notification.title} />
      <Overlay.Content>
        <Stack spacing={3}>
          <Box sx={{ backgroundColor, borderRadius: 1, p: 2 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              <NotificationBody
                body={notification.body}
                segments={notification.bodySegments}
                onNavigate={closeOverlay}
              />
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <DetailField label={t('labels_source')} value={tTable('source_workflow')} href={sourceHref} onNavigate={closeOverlay} />
            {notification.createdAt && (
              <DetailField
                label={t('labels_received')}
                value={format.dateTime(new Date(notification.createdAt), 'dateDotTime')}
              />
            )}
          </Grid>
        </Stack>
      </Overlay.Content>
      <Overlay.Actions>
        <Button onClick={closeOverlay} variant="outlined">
          {t('close')}
        </Button>
      </Overlay.Actions>
    </Overlay>
  )
})

type DetailFieldProps = {
  label: string
  value: string
  href?: string
  onNavigate?: () => void
}

function DetailField({ label, value, href, onNavigate }: DetailFieldProps) {
  return (
    <Grid item xs={6}>
      <Stack>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {href ? (
          <Link href={href} onClick={onNavigate} sx={{ fontSize: '0.875rem' }}>
            {value}
          </Link>
        ) : (
          <Typography variant="body2">{value}</Typography>
        )}
      </Stack>
    </Grid>
  )
}

export default MergedNotificationDetailOverlay
